import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react"; // Lucide Icons
import Select from "react-select";

export const Form = ({
  fields,
  formData,
  setFormData,
  onSubmit,
  btnText,
  isLoading,
  disabled,
  grid,
  beforeSubmit,
}) => {
  const [errors, setErrors] = useState({});
  const [passwordVisible, setPasswordVisible] = useState(false);
  useEffect(() => {
    // Only initialize formData if it's empty
    setFormData((prev) => {
      const isEmpty = !prev || Object.keys(prev).length === 0;
      if (isEmpty) {
        const initialized = fields.reduce((acc, field) => {
          acc[field.name] =
            field.type === "checkbox"
              ? (field.value ?? false)
              : field.type === "radio"
                ? (field.value ?? "")
                : field.type === "file"
                  ? null
                  : (field.value ?? "");
          return acc;
        }, {});
        return initialized;
      }
      return prev;
    });
  }, [fields]);

  const handleChange = (e) => {
    const { name, type, value, checked, files } = e.target;
    const newValue =
      type === "checkbox" ? checked : type === "file" ? files[0] : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    fields.forEach((field) => {
      if (field.required) {
        const value = formData[field.name];

        const isEmpty =
          (field.type === "file" && !value) ||
          (field.type === "checkbox" && !value) ||
          (typeof value === "string" && value.trim() === "") ||
          value === null ||
          value === undefined;

        if (isEmpty) {
          newErrors[field.name] = `${field.label || field.name} is required`;
        }

        // Password length validation
        if (field.type === "password" && value && value.length < 8) {
          newErrors[field.name] = "Password must be at least 8 characters";
        }
      }
    });

    setErrors(newErrors);

    // Only submit if no errors
    if (Object.keys(newErrors).length === 0) {
      onSubmit(formData);
    }
  };

  const validateField = (name, value, required) => {
    if (required && (!value || value === "")) {
      setErrors((prev) => ({ ...prev, [name]: "This field is required" }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={
        grid
          ? "grid grid-cols-2 gap-4 p-0 rounded-lg"
          : "space-y-4 p-0 rounded-lg max-w-lg mx-auto"
      }
    >
      {fields.map((field, index) => {
        if (field.hidden) return null;
        return (
          <div key={index} className="flex flex-col relative">
            {/* {field.label && field.name == "checkbox" && <label className="font-nexa mb-1 text-gray-700">{field.label}</label>} */}
            {field.label && field.type !== "checkbox" && (
              <label className="font-nexa mb-1 text-gray-700">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            {field.type === "textarea" ? (
              <textarea
                name={field.name}
                placeholder={field.placeholder}
                value={formData[field.name]}
                onChange={handleChange}
                className="font-nexa border p-2 rounded bg-transparent"
                data-testid={field?.testId}
              />
            ) : field.type === "select" ? (
              field.searchable ? (
                <div data-testid={field?.testId}>
                  <Select
                    name={field.name}
                    value={
                      field.options?.find(
                        (opt) => opt.value === formData[field.name],
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      setFormData((prev) => ({
                        ...prev,
                        [field.name]: selectedOption
                          ? selectedOption.value
                          : "", // store only value
                      }))
                    }
                    options={field.options}
                    // data-testid={field?.testId}
                    placeholder={field.placeholder || "Select..."}
                    isSearchable={true}
                    isClearable={true}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "transparent",
                        borderColor: "#ccc",
                        minHeight: "40px",
                        boxShadow: "none",
                        "&:hover": { borderColor: "#888" },
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 50,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused ? "#f5f5f5" : "white",
                        color: "black",
                      }),
                    }}
                  />
                </div>
              ) : (
                // Regular native <select>
                <select
                  name={field.name}
                  value={formData[field.name]}
                  data-testid={field?.testId}
                  onChange={handleChange}
                  className="font-nexa border p-2 rounded bg-transparent"
                >
                  {(field.options || []).map((option, idx) => (
                    <option key={idx} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )
            ) : field.type === "radio" ? (
              <div className="flex gap-4">
                {field.options.map((option, idx) => (
                  <label key={idx} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={field.name}
                      value={option.value}
                      checked={formData[field.name] === option.value}
                      onChange={handleChange}
                      className="font-nexa accent-blue-500 bg-transparent"
                      data-testid={field?.testId}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            ) : field.type === "checkbox" ? (
              <label className="font-nexa flex items-center gap-2">
                <input
                  type="checkbox"
                  name={field.name}
                  data-testid={field?.testId}
                  checked={formData[field.name] ?? false}
                  onChange={handleChange}
                  onBlur={() =>
                    validateField(
                      field.name,
                      formData[field.name],
                      field.required,
                    )
                  }
                  className="
                peer relative h-5 w-5 cursor-pointer appearance-none rounded-md
                border border-gray-400 bg-white
                 checked:bg-green
                hover:border-green-400
                transition-all duration-200
              "
                />
                {/* Custom checkmark icon */}
                <svg
                  className="absolute hidden h-3 w-3 text-white pointer-events-none peer-checked:block"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="green"
                  style={{ marginLeft: "4px" }}
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            ) : field.type === "file" ? (
              <input
                type="file"
                name={field.name}
                onChange={handleChange}
                onBlur={(e) =>
                  validateField(field.name, e.target.files[0], field.required)
                }
                className="font-nexa border p-2 rounded bg-transparent"
                data-testid={field?.testId}
              />
            ) : field.type === "password" ? (
              <div className="relative">
                <input
                  type={passwordVisible ? "text" : "password"}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={formData[field.name] ?? ""}
                  onChange={handleChange}
                  className="font-nexa border p-2 rounded w-full pr-10 bg-transparent"
                  data-testid={field?.testId}
                />
                <button
                  type="button"
                  className="font-nexa absolute right-2 top-3 text-gray-600 hover:text-black"
                  onClick={() => setPasswordVisible((prev) => !prev)}
                >
                  {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            ) : (
              <input
                type={field.type}
                name={field.name}
                placeholder={field.placeholder}
                value={formData[field.name] ?? ""}
                onChange={handleChange}
                onBlur={(e) =>
                  validateField(field.name, e.target.value, field.required)
                }
                className="font-nexa border p-2 rounded bg-transparent"
                data-testid={field?.testId}
                required={field.required}
              />
            )}

            {errors[field.name] && (
              <span className="font-nexa text-error text-sm">
                {errors[field.name]}
              </span>
            )}
          </div>
        );
      })}

      {beforeSubmit}

      <button
        type="submit"
        disabled={isLoading || disabled}
        className={`font-nexa bg-primary-dark text-white px-4 py-2 rounded self-end flex items-center justify-center gap-2 ${grid ? "col-span-2" : ""} ${isLoading || disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {isLoading ? (
          <Loader2 className="animate-spin" size={18} />
        ) : (
          btnText || "Submit"
        )}
      </button>
    </form>
  );
};
